import { useState, useCallback, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Target,
  Zap,
  Activity,
  Package,
  ArrowUpRight,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PaywallModalTrigger } from "./PaywallModalTrigger";
import {
  ChartContainer,
} from "@/components/ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  Cell,
} from "recharts";

const CACHE_KEY = "cfo_command_center_v2";
const CURRENCY = "K";
const GREEN = "hsl(142, 76%, 36%)";
const RED = "hsl(0, 72%, 51%)";
const AMBER = "hsl(38, 92%, 50%)";

export type TimeRange = "7D" | "1M" | "3M" | "6M" | "1Y" | "All";

export function getSinceDate(timeRange: TimeRange): Date {
  const now = new Date();
  const d = new Date(now);
  switch (timeRange) {
    case "7D": d.setDate(d.getDate() - 7); break;
    case "1M": d.setMonth(d.getMonth() - 1); break;
    case "3M": d.setMonth(d.getMonth() - 3); break;
    case "6M": d.setMonth(d.getMonth() - 6); break;
    case "1Y": d.setFullYear(d.getFullYear() - 1); break;
    case "All": d.setFullYear(d.getFullYear() - 10); break;
    default: d.setMonth(d.getMonth() - 1);
  }
  return d;
}

// ─── Types (match Edge Function response) ────────────────────────────────────
interface TimeSeriesPoint {
  date: string;
  value: number;
}

interface CFOMetrics {
  revenue?: { last7: number; prior7: number; velocityPct: number; projectedNext7Days: number };
  profit?: { totalExpenses: number; grossProfit: number; marginPct: number; expenseRatio: number };
  inventory?: {
    turnoverRate30d: number;
    totalUnitsSold30d: number;
    totalInventoryUnits: number;
    slowMoversCount: number;
    slowMovers?: { itemId: string; itemName: string; unitsSold: number }[];
    deadStockCount: number;
    cashAtRiskTotal: number;
    deadStockByCategory?: Record<string, number>;
  };
  health?: {
    compositeHealthScore: number;
    phase: string;
    velocityScore: number;
    marginScore: number;
    turnoverScore: number;
    riskPenalty: number;
  };
  topItems?: { name: string; revenue: number }[];
  topPerformer?: { name: string; revenue: number; velocityContribution: number } | null;
}

interface CFOAI {
  analysis?: string;
  recommendations?: string[];
  priority?: string;
  focus_area?: string;
}

function sanitizeMoneySafetyText(text: string | undefined): string | undefined {
  if (!text) return text;
  // Remove unrealistic "you can survive for X months" phrasing from older AI runs
  return text.replace(/survive for [0-9.,]+\s+months[^.]*\./gi, "Your cash buffer looks strong at the current spending level, but keep aiming for a 3–6 month safety cushion.");
}

interface CFOCommandResult {
  revenueSeries?: TimeSeriesPoint[];
  expenseSeries?: TimeSeriesPoint[];
  cashFlowSeries?: TimeSeriesPoint[];
  velocity?: { last7Revenue: number; prior7Revenue: number; changePct: number };
  cashAtRisk?: number;
  topPerformer?: { name: string; revenue: number; velocityContribution: number } | null;
  strategicDirective?: string;
  status?: string;
  metrics?: CFOMetrics;
  ai?: CFOAI;
  directives?: { type: string; text: string }[];
}

function loadCached(): CFOCommandResult | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CFOCommandResult;
  } catch {
    return null;
  }
}

function saveCached(result: CFOCommandResult) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(result));
  } catch {
    // ignore
  }
}

function formatK(n: number): string {
  return `${CURRENCY} ${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatPct(n: number): string {
  const s = n >= 0 ? `+${n.toFixed(1)}%` : `${n.toFixed(1)}%`;
  return s;
}

function healthColor(score: number): string {
  if (score > 50) return "text-emerald-600 dark:text-emerald-400";
  if (score > 20) return "text-emerald-600 dark:text-emerald-400";
  if (score > 0) return "text-amber-600 dark:text-amber-400";
  if (score > -20) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function healthBadgeVariant(phase: string): "default" | "secondary" | "destructive" | "outline" {
  if (phase === "Expansion" || phase === "Growth") return "default";
  if (phase === "Stable") return "secondary";
  if (phase === "Stagnation Risk") return "outline";
  return "destructive";
}

interface AIInsightsProps {
  timeRange?: TimeRange;
  dashboardSummary?: string;
  isPro?: boolean;
  inventoryCount?: number;
}

export default function AIInsights({ timeRange = "7D", dashboardSummary: _dashboardSummary, isPro = true, inventoryCount = 0 }: AIInsightsProps) {
  const { businessId } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CFOCommandResult | null>(() => loadCached());

  const runAnalysis = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({ title: "Analysis failed", description: "Please sign in again and try again.", variant: "destructive" });
        setLoading(false);
        return;
      }
      // Send both Authorization and x-auth-token (fallback if proxy strips Authorization on mobile)
      const { data, error } = await supabase.functions.invoke("get-ai-insights", {
        body: {},
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "x-auth-token": session.access_token,
        },
      });
      if (error) {
        const bodyMsg = data && typeof data === "object" && "error" in data
          ? (typeof (data as { error?: unknown }).error === "string"
            ? (data as { error: string }).error
            : (data as { error?: { message?: string } }).error?.message)
          : null;
        const msg = bodyMsg || error.message || "Server error. If this persists, the AI service may need configuration.";
        toast({ title: "Analysis failed", description: msg, variant: "destructive" });
        return;
      }
      if (data?.error) {
        const msg = typeof data.error === "string" ? data.error : (data.error as { message?: string }).message ?? "Unknown error";
        toast({ title: "Analysis failed", description: msg, variant: "destructive" });
        return;
      }
      const response = (data ?? {}) as CFOCommandResult;
      setResult(response);
      saveCached(response);
    } catch (err) {
      console.error("Front-end Fetch Error:", err);
      toast({
        title: "Analysis failed",
        description: err instanceof Error ? err.message : "Network or request error.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [businessId, toast]);

  const metrics = result?.metrics;
  const ai = result?.ai
    ? {
        ...result.ai,
        analysis: sanitizeMoneySafetyText(result.ai.analysis),
        recommendations: (result.ai.recommendations ?? []).map((r) => sanitizeMoneySafetyText(r) ?? r),
      }
    : undefined;
  const changePct = result?.velocity?.changePct ?? metrics?.revenue?.velocityPct ?? 0;
  const revenueLast7 = result?.velocity?.last7Revenue ?? metrics?.revenue?.last7 ?? 0;
  const projectedNext7 = metrics?.revenue?.projectedNext7Days ?? 0;

  const lineChartData = useMemo(() => {
    const rev = result?.revenueSeries ?? [];
    const exp = result?.expenseSeries ?? [];
    const byDate: Record<string, { date: string; revenue: number; expenses: number }> = {};
    rev.forEach(({ date, value }) => {
      byDate[date] = { date, revenue: value, expenses: 0 };
    });
    exp.forEach(({ date, value }) => {
      if (!byDate[date]) byDate[date] = { date, revenue: 0, expenses: 0 };
      byDate[date].expenses = value;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [result?.revenueSeries, result?.expenseSeries]);

  if (!isPro) {
    const isLearningPhase = inventoryCount < 5;
    return (
      <Card data-testid="bi-analyst-card" className="backdrop-blur-md bg-background/80 border-2 border-primary/30 shadow-sm rounded-xl overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <BarChart3 className="h-5 w-5 text-primary" /> BI Analyst
            <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Free</Badge>
            <span className="text-muted-foreground text-sm font-normal">— Upgrade to Pro</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-8 pb-12 pt-4">
          {isLearningPhase ? (
            <p data-testid="bi-analyst-empty-state" className="text-lg font-medium text-foreground text-center max-w-md mx-auto py-8">
              Your BI Analyst is learning your shop—check back after adding 5+ items.
            </p>
          ) : (
            <>
              <p data-testid="bi-analyst-teaser" className="text-lg font-medium text-foreground text-center max-w-md mx-auto py-8">
                Your BI Analyst has insights and recommendations waiting
              </p>
              <p className="text-sm text-muted-foreground text-center max-w-md mx-auto mb-6">
                Unlock full BI Analyst access to see AI insights, health scores, revenue trends, and actionable recommendations.
              </p>
              <div className="flex justify-center">
                <PaywallModalTrigger />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const barChartConfig = useMemo(
    () => ({
      value: { label: "Cash Flow", color: "hsl(217, 91%, 60%)" },
    }),
    []
  );
  const lineChartConfig = useMemo(
    () => ({
      revenue: { label: "Revenue", color: GREEN },
      expenses: { label: "Expenses", color: RED },
    }),
    []
  );

  if (!result && !loading) {
    return (
      <Card className="backdrop-blur-md bg-background/80 border border-border/60 shadow-sm rounded-xl overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-8">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <BarChart3 className="h-5 w-5 text-primary" /> Business Analysis
          </CardTitle>
          <Button variant="secondary" size="sm" onClick={runAnalysis} disabled={loading}>
            Run BI Analysis
          </Button>
        </CardHeader>
        <CardContent className="px-8 pb-12 pt-4">
          <p className="text-lg font-medium text-foreground text-center max-w-md mx-auto py-8">
            Welcome to your Executive Suite
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-md mx-auto">
            Click &quot;Run BI Analysis&quot; to load the 7-day snapshot, AI insights, and charts. For the official monthly Business Health Score, go to Reports.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading && !result) {
    return (
      <Card className="backdrop-blur-md bg-background/80 border border-border/60 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="flex flex-col items-center justify-center py-20 gap-4">
          <span className="h-12 w-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Building Business Analysis…</span>
        </CardContent>
      </Card>
    );
  }

  const health = metrics?.health;
  const healthScore = health?.compositeHealthScore ?? 0;
  const phase = health?.phase ?? result?.status ?? "Stable";

  return (
    <div data-testid="bi-analyst-card" className="space-y-8">
      {/* Title + Run button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-display font-bold">Business Analysis</h2>
            <Badge variant="default" className="text-[9px] px-1.5 py-0">Pro</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Live 7-day snapshot for quick decisions. For the official monthly Business Health Score and P&amp;L, see Reports.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={runAnalysis} disabled={loading}>
          {loading ? (
            <>
              <span className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              Analysing…
            </>
          ) : (
            "Run BI Analysis"
          )}
        </Button>
      </div>

      {/* 1. Executive Command Bar — 5 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border border-border/60 shadow-sm" title="Same formula as Reports (velocity, margin, turnover, risk) but using last 7 days. Official monthly score is in Reports.">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">7-Day Health Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className={`text-3xl font-extrabold tabular-nums ${healthColor(healthScore)}`}>{healthScore}</div>
            <Badge variant={healthBadgeVariant(phase)} className="text-[10px]">{phase}</Badge>
            {health && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground pt-1">
                <span>V:{Math.round(health.velocityScore)}</span>
                <span>M:{Math.round(health.marginScore)}</span>
                <span>T:{Math.round(health.turnoverScore)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border/60 shadow-sm" title="Total sales revenue in the last 7 days. Change % vs prior 7 days.">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Revenue (7D)</CardTitle>
          </CardHeader>
          <CardContent className="flex items-end justify-between gap-2">
            <span className="text-xl font-bold tabular-nums text-foreground">{formatK(revenueLast7)}</span>
            <span className={`flex items-center gap-0.5 text-sm font-semibold ${changePct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {changePct > 0 && <TrendingUp className="h-4 w-4" />}
              {changePct < 0 && <TrendingDown className="h-4 w-4" />}
              {changePct === 0 && <Minus className="h-4 w-4" />}
              {formatPct(changePct)}
            </span>
          </CardContent>
        </Card>

        <Card className="border border-border/60 shadow-sm" title="Gross profit = Revenue minus expenses. Margin % = profit as % of revenue.">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Profit & Margin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0.5">
            <p className={`text-lg font-bold tabular-nums ${(metrics?.profit?.grossProfit ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {formatK(metrics?.profit?.grossProfit ?? 0)}
            </p>
            <p className="text-xs text-muted-foreground">Margin {(metrics?.profit?.marginPct ?? 0).toFixed(1)}%</p>
          </CardContent>
        </Card>

        <Card className="border border-border/60 shadow-sm" title="Cash tied up in items with no sales in 30 days. Dead stock = items not selling; consider markdowns or liquidation.">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Inventory Risk</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0.5">
            {(() => {
              const cashAtRisk = result?.cashAtRisk ?? metrics?.inventory?.cashAtRiskTotal ?? 0;
              const deadCount = metrics?.inventory?.deadStockCount ?? 0;
              const isGood = deadCount === 0 && cashAtRisk === 0;
              return (
                <>
                  <p className={`text-lg font-bold tabular-nums ${isGood ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                    {formatK(cashAtRisk)}
                  </p>
                  <p className={`text-xs ${isGood ? "text-emerald-600/90 dark:text-emerald-400/90" : "text-muted-foreground"}`}>
                    {deadCount} dead stock items
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>

        <Card className="border border-border/60 shadow-sm" title="Estimated revenue for the next 7 days using current velocity (last 7d vs prior 7d).">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">7-Day Projection</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold tabular-nums text-foreground">{formatK(projectedNext7)}</p>
            <p className="text-[10px] text-muted-foreground">Projected next 7 days</p>
          </CardContent>
        </Card>
      </div>

      {/* 2. Strategic AI Panel — full width, high contrast */}
      <Card data-testid="bi-analyst-full-insight" className="border-2 border-primary/20 bg-primary/5 shadow-sm overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Strategic AI Panel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={ai?.priority === "high" ? "destructive" : ai?.priority === "low" ? "secondary" : "default"}>
                  {ai?.priority ?? "medium"}
                </Badge>
                <Badge variant="outline" className="text-[10px] capitalize">
                  {ai?.focus_area ?? "operational_review"}
                </Badge>
              </div>
              <p className="text-sm leading-relaxed text-foreground">{ai?.analysis ?? "No analysis available."}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recommendations</p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-foreground">
                {(ai?.recommendations ?? []).map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </div>
          </div>
          <div data-testid="bi-analyst-action-step" className="rounded-lg border-2 border-amber-500/40 bg-amber-500/10 p-6">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2">Actionable Directive</p>
            <p className="text-xl md:text-2xl font-bold leading-snug text-foreground">
              {result?.strategicDirective ?? "Review velocity and adjust stock rotation."}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 3. Financial Performance — two column: line chart + bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border/60 shadow-sm">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Revenue vs Expenses</CardTitle>
            <p className="text-xs text-muted-foreground">Time series from BI data</p>
          </CardHeader>
          <CardContent>
            {lineChartData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No series data</div>
            ) : (
              <ChartContainer id="cfo-revenue-expense" config={lineChartConfig} className="h-[260px] w-full">
                <LineChart data={lineChartData} margin={{ left: 12, right: 12, top: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value: number) => [formatK(value), ""]}
                    labelFormatter={(label) => label}
                    contentStyle={{ fontSize: "11px" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                  <Line type="monotone" dataKey="revenue" stroke={GREEN} strokeWidth={2} dot={{ r: 2 }} name="Revenue" />
                  <Line type="monotone" dataKey="expenses" stroke={RED} strokeWidth={2} dot={{ r: 2 }} name="Expenses" />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border/60 shadow-sm">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Cash Flow</CardTitle>
            <p className="text-xs text-muted-foreground">Negative bars in red</p>
          </CardHeader>
          <CardContent>
            {!(result?.cashFlowSeries?.length) ? (
              <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No cash flow data</div>
            ) : (
              <ChartContainer id="cfo-cashflow" config={barChartConfig} className="h-[260px] w-full">
                <BarChart
                  data={result.cashFlowSeries}
                  margin={{ left: 12, right: 12, top: 8, bottom: 4 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => v?.slice(5) ?? v} />
                  <Tooltip formatter={(value: number) => [formatK(value), "Cash Flow"]} contentStyle={{ fontSize: "11px" }} />
                  <Bar dataKey="value" name="Cash Flow" radius={[0, 4, 4, 0]} maxBarSize={24}>
                    {(result.cashFlowSeries ?? []).map((entry, index) => (
                      <Cell key={index} fill={entry.value < 0 ? RED : "hsl(217, 91%, 60%)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 4. Profit & Inventory Intelligence Grid — 3 panels */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-border/60 shadow-sm" title="Units sold in 30 days ÷ total inventory units. Higher = stock moving faster; low = overstock or slow sellers.">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4" /> Turnover
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-2xl font-bold tabular-nums">{(metrics?.inventory?.turnoverRate30d ?? 0).toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">30-day turnover rate</p>
            <Progress value={Math.min(100, ((metrics?.inventory?.turnoverRate30d ?? 0) * 100))} className="h-2" />
          </CardContent>
        </Card>

        <Card className={`border shadow-sm ${(metrics?.inventory?.deadStockCount ?? 0) === 0 ? "border-emerald-200 dark:border-emerald-900/50" : "border-red-200 dark:border-red-900/50"}`} title="Items with zero sales in the last 30 days but still in inventory. Cash at risk = stock × buying price; consider promotions or write-offs.">
          <CardHeader className="pb-1">
            <CardTitle className={`text-sm flex items-center gap-2 ${(metrics?.inventory?.deadStockCount ?? 0) === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
              <AlertTriangle className="h-4 w-4" /> Dead Stock
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(() => {
              const deadCount = metrics?.inventory?.deadStockCount ?? 0;
              const cashAtRisk = metrics?.inventory?.cashAtRiskTotal ?? result?.cashAtRisk ?? 0;
              const isGood = deadCount === 0 && cashAtRisk === 0;
              return (
                <>
                  <p className={`text-lg font-bold tabular-nums ${isGood ? "text-emerald-600 dark:text-emerald-400" : ""}`}>{deadCount} items</p>
                  <p className={`text-sm font-semibold ${isGood ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{formatK(cashAtRisk)}</p>
                </>
              );
            })()}
            {metrics?.inventory?.deadStockByCategory && Object.keys(metrics.inventory.deadStockByCategory).length > 0 && (
              <div className="pt-2 border-t border-border/50 space-y-1">
                {Object.entries(metrics.inventory.deadStockByCategory).map(([cat, val]) => (
                  <div key={cat} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{cat}</span>
                    <span className="font-medium">{formatK(val)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border/60 shadow-sm" title="Items that sold fewer than 2 units in 30 days. May need better placement, pricing, or could become dead stock.">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2">
              <Package className="h-4 w-4" /> Slow Movers
            </CardTitle>
            <p className="text-[10px] text-muted-foreground">&lt; 2 units sold (30d)</p>
          </CardHeader>
          <CardContent>
            {!(metrics?.inventory?.slowMovers?.length) ? (
              <p className="text-xs text-muted-foreground">None</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-1.5 font-medium">Item</th>
                      <th className="text-right py-1.5 font-medium">Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(metrics.inventory.slowMovers ?? []).slice(0, 8).map((row) => (
                      <tr key={row.itemId} className="border-b border-border/30">
                        <td className="py-1 truncate max-w-[120px]">{row.itemName}</td>
                        <td className="py-1 text-right tabular-nums">{row.unitsSold}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 5. Revenue Intelligence — bar chart + spotlight */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border/60 shadow-sm" title="Sales revenue per product in the last 7 days. Use this to see which items drive the most income.">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Revenue by Item (7D)</CardTitle>
          </CardHeader>
          <CardContent>
            {!(metrics?.topItems?.length) ? (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">No top items data</div>
            ) : (
              <ChartContainer id="cfo-top-items" config={{ revenue: { label: "Revenue", color: GREEN } }} className="h-[220px] w-full">
                <BarChart
                  data={metrics.topItems}
                  margin={{ left: 8, right: 8, top: 8, bottom: 4 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "currentColor" }} tickLine={false} axisLine={false} width={80} />
                  <Tooltip formatter={(value: number) => [formatK(value), "Revenue"]} contentStyle={{ fontSize: "11px" }} />
                  <Bar dataKey="revenue" name="Revenue" fill={GREEN} radius={[0, 4, 4, 0]} maxBarSize={28} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 border-emerald-500/30 bg-emerald-500/5 shadow-sm" title="The single item that contributed most to revenue growth (last 7d vs prior 7d). Double down on promotion or stock for this item.">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <ArrowUpRight className="h-4 w-4" /> Primary Driver of Growth
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-2xl font-bold text-foreground">{result?.topPerformer?.name ?? metrics?.topPerformer?.name ?? "—"}</p>
            <p className="text-sm text-muted-foreground">
              {result?.topPerformer
                ? `${formatK(result.topPerformer.revenue)} revenue · ${formatK(result.topPerformer.velocityContribution)} velocity lift`
                : "Single item driving largest velocity increase"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
